import React, { useRef, useState } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { X, Hammer, MoveHorizontal } from "lucide-react";

const projects = [
  { src: "/gallery/kitchen_interior.jpg", wip: "/gallery/kitchen_interior_wip.jpg", title: "Custom Kitchen", meta: "Interior · Cabinetry & Stone", topic: "kitchen",
    story: "A custom interior kitchen with honed black granite, full-height painted cabinetry, a pro range and hand-selected pendant lighting — built around how the family actually lives and entertains.",
    finishes: ["Black Granite", "Custom Cabinetry", "Pro Range Suite", "Designer Pendants"] },
  { src: "/gallery/fireplace_new.jpg", wip: "/gallery/fireplace_new_wip.jpg", title: "Stone Fireplace", meta: "Stacked Stone & LED",
    story: "A floor-to-ceiling white stacked-stone fireplace with a full-width mounted display, hidden LED backlighting, and a sleek black linear firebox — a modern centerpiece the whole room gathers around.",
    finishes: ["White Stacked Stone", "Hidden LED Backlight", "Linear Firebox", "Integrated TV Niche"] },
  { src: "/gallery/staircase.jpg", wip: "/gallery/staircase_wip.jpg", title: "Custom Staircase", meta: "Trim & Millwork",
    story: "A reimagined staircase with hand-built newel posts, turned white balusters and a rich stained handrail — the kind of millwork that sets the tone the moment you walk in.",
    finishes: ["Turned Balusters", "Stained Handrail", "Custom Newel Posts", "Trim Carpentry"] },
  { src: "/gallery/closet_oak.jpg", wip: "/gallery/closet_oak_wip.jpg", title: "Walk-In Wardrobe", meta: "Cabinetry & Hidden Door",
    story: "A seamless oak wardrobe wall where a full-height concealed door opens to the dressing room beyond — flat-panel millwork with no visible hardware, just clean grain-matched lines.",
    finishes: ["Grain-Matched Oak", "Concealed Door", "Flat-Panel Fronts", "Integrated Storage"] },
  { src: "/gallery/pantry.webp", wip: "/gallery/pantry_wip.jpg", title: "Custom Pantry", meta: "Cabinetry & Built-Ins",
    story: "Floor-to-ceiling custom cabinetry with a live-edge wood counter, soft-close everything, and integrated lighting for a walk-in that works as hard as it looks.",
    finishes: ["Custom Cabinetry", "Walnut Counter", "Integrated LED", "Soft-Close Hardware"] },
  { src: "/gallery/ok_marble.jpg", wip: "/gallery/ok_marble_wip.jpg", title: "Outdoor Kitchen", meta: "Marble & Stone",
    story: "A resort-grade outdoor kitchen built for entertaining — Saint Clair Fleuri countertops, pearl-grey marble wall caps, and full stone veneer wrap.",
    finishes: ["Pearl-Grey Marble", "Stone Veneer", "Stainless Grill Suite", "Custom LED"] },
  { src: "/gallery/patio_flagstone.jpg", title: "Stone Patio & Walk", meta: "Flagstone Hardscape",
    story: "A hand-laid flagstone walk and seat wall with a natural-stone cap — every piece fit and leveled by hand for a patio that looks like it grew out of the landscape.",
    finishes: ["Natural Flagstone", "Stone Seat Wall", "Hand-Fit Coursing", "Stone Cap"] },
  { src: "/gallery/greatroom.jpg", wip: "/gallery/greatroom_wip.jpg", title: "The Great Room", meta: "Whole-Home Design", topic: "wholehome",
    story: "The heart of a whole-home remodel — arched floor-to-ceiling glazing, coffered ceilings, and elaborate molding that ties the entire residence together.",
    finishes: ["Arched Glazing", "Coffered Ceiling", "Elaborate Molding", "Venetian Plaster"] },
];

function FinishedPanel({ p, index, count, onOpen }) {
  return (
    <div className="absolute inset-0 group cursor-pointer" onClick={() => onOpen(p)} data-cursor="View" data-testid={`work-compare-${index}`}>
      <img src={p.src} alt={p.title} draggable="false" loading="lazy"
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/5 to-transparent pointer-events-none" />
      <div className="absolute bottom-0 left-0 p-6 pointer-events-none">
        <div className="overline mb-2">{String(index + 1).padStart(2, "0")} / {String(count).padStart(2, "0")}</div>
        <div className="font-display text-2xl sm:text-3xl text-white">{p.title}</div>
        <div className="text-sm text-[var(--text-2)] mt-1">{p.meta}</div>
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); onOpen(p); }}
        data-testid={`work-open-${index}`}
        className="absolute bottom-6 right-6 z-[3] glass rounded-full px-4 py-2 text-[10px] tracking-[0.2em] uppercase text-white hover:text-[var(--gold)] transition-colors"
      >
        View Story
      </button>
    </div>
  );
}

function Panel({ p, progress, index, count, onOpen }) {
  const y = useTransform(progress, [0, 1], ["-5%", "5%"]);
  const wrap = useRef(null);
  const [pos, setPos] = useState(0.5); // 0..1  left=finished, right=construction
  const drag = useRef({ active: false, moved: false, startX: 0 });

  const setFromClientX = (clientX) => {
    const r = wrap.current.getBoundingClientRect();
    setPos(Math.max(0, Math.min(1, (clientX - r.left) / r.width)));
  };

  const onDown = (e) => {
    drag.current = { active: true, moved: false, startX: e.clientX };
    setFromClientX(e.clientX);
    wrap.current.setPointerCapture?.(e.pointerId);
  };
  const onMove = (e) => {
    if (!drag.current.active) return;
    if (Math.abs(e.clientX - drag.current.startX) > 4) drag.current.moved = true;
    setFromClientX(e.clientX);
  };
  const onUp = (e) => {
    const wasDragging = drag.current.active;
    const moved = drag.current.moved;
    drag.current.active = false;
    wrap.current.releasePointerCapture?.(e.pointerId);
    if (wasDragging && !moved) onOpen(p); // treat as tap → open story
  };

  return (
    <motion.div style={{ y }} className="relative shrink-0 w-[80vw] sm:w-[52vw] lg:w-[38vw] h-[62vh] rounded-xl overflow-hidden border border-white/10"
      data-topic={p.topic || undefined} data-testid={`work-item-${index}`}>
      {!p.wip ? (
        <FinishedPanel p={p} index={index} count={count} onOpen={onOpen} />
      ) : (
      <div
        ref={wrap}
        className="absolute inset-0 select-none touch-none cursor-ew-resize"
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        data-cursor="Drag"
        data-testid={`work-compare-${index}`}
      >
        {/* finished (base, left side) */}
        <img src={p.src} alt={p.title} draggable="false" loading="lazy" className="absolute inset-0 w-full h-full object-cover pointer-events-none" />
        {/* under construction, revealed on the right of the handle */}
        <img
          src={p.wip}
          alt={`${p.title} under construction`}
          draggable="false"
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
          style={{ clipPath: `inset(0 0 0 ${pos * 100}%)` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/5 to-transparent pointer-events-none" />

        {/* stage labels */}
        <div className="absolute top-4 left-4 text-[10px] tracking-[0.22em] uppercase text-white/70 pointer-events-none">Finished</div>
        <div className="absolute top-4 right-4 flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase text-[var(--gold)] pointer-events-none">
          <Hammer size={11} /> Under Construction
        </div>

        {/* handle */}
        <div className="absolute top-0 bottom-0 pointer-events-none" style={{ left: `${pos * 100}%` }}>
          <div className="absolute inset-y-0 -translate-x-1/2 w-[2px] bg-[var(--gold)]/90 shadow-[0_0_18px_rgba(214,164,65,0.7)]" />
          <div className="absolute top-1/2 left-0 -translate-x-1/2 -translate-y-1/2 w-11 h-11 rounded-full glass border border-[var(--gold)]/60 grid place-items-center text-[var(--gold)]">
            <MoveHorizontal size={16} />
          </div>
        </div>

        {/* title / open cue */}
        <div className="absolute bottom-0 left-0 p-6 pointer-events-none">
          <div className="overline mb-2">{String(index + 1).padStart(2, "0")} / {String(count).padStart(2, "0")}</div>
          <div className="font-display text-2xl sm:text-3xl text-white">{p.title}</div>
          <div className="text-sm text-[var(--text-2)] mt-1">{p.meta}</div>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onOpen(p); }}
          data-testid={`work-open-${index}`}
          className="absolute bottom-6 right-6 z-[3] glass rounded-full px-4 py-2 text-[10px] tracking-[0.2em] uppercase text-white hover:text-[var(--gold)] transition-colors"
        >
          View Story
        </button>
      </div>
      )}
    </motion.div>
  );
}

function CaseView({ project, onClose }) {
  const [showWip, setShowWip] = useState(false);
  return (
    <motion.div className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-8" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="case-view">
      <div className="absolute inset-0 bg-black/85 backdrop-blur-xl" onClick={onClose} />
      <motion.div
        initial={{ y: 40, scale: 0.97 }} animate={{ y: 0, scale: 1 }} exit={{ y: 30, opacity: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="relative glass rounded-2xl overflow-hidden w-full max-w-5xl max-h-[88vh] grid grid-cols-1 lg:grid-cols-2"
      >
        <div className="relative h-64 lg:h-full min-h-[260px]">
          {project.wip && <img src={project.wip} alt="" className="absolute inset-0 w-full h-full object-cover" />}
          <img src={project.src} alt={project.title} className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${showWip && project.wip ? "opacity-0" : "opacity-100"}`} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent lg:bg-gradient-to-r pointer-events-none" />
          {project.wip && (
          <button
            onClick={(e) => { e.stopPropagation(); setShowWip((v) => !v); }}
            data-testid="case-wip-toggle"
            className="absolute bottom-4 left-4 flex items-center gap-2 glass rounded-full px-4 py-2 text-[10px] tracking-[0.2em] uppercase text-white hover:text-[var(--gold)] transition-colors"
          >
            <Hammer size={12} /> {showWip ? "Show finished" : "See it under construction"}
          </button>
          )}
        </div>
        <div className="p-8 lg:p-12 overflow-y-auto">
          <div className="overline mb-4">{project.meta}</div>
          <h3 className="font-display text-3xl sm:text-4xl gold-fill mb-5">{project.title}</h3>
          <p className="text-white/75 leading-relaxed mb-8">{project.story}</p>
          <div className="overline mb-4">Finishes Used</div>
          <div className="flex flex-wrap gap-2 mb-10">
            {project.finishes.map((f) => (
              <span key={f} className="text-xs px-3 py-1.5 rounded-full border border-[var(--gold)]/30 text-white/80">{f}</span>
            ))}
          </div>
          <button
            onClick={() => { onClose(); const el = document.getElementById("contact"); if (el) el.scrollIntoView({ behavior: "smooth" }); }}
            data-testid="case-cta"
            className="btn-gold rounded-full px-7 py-3.5 text-xs tracking-[0.18em] uppercase font-medium"
          >
            Start a project like this
          </button>
        </div>
        <button onClick={onClose} data-testid="case-close" data-cursor="Close" className="absolute top-4 right-4 w-10 h-10 rounded-full glass grid place-items-center text-white hover:text-[var(--gold)]">
          <X size={18} />
        </button>
      </motion.div>
    </motion.div>
  );
}

export default function ProjectGallery() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const n = projects.length;
  const shift = n * 40 - 82;
  const x = useTransform(scrollYProgress, [0.05, 1], ["4vw", `-${shift}vw`]);
  const [selected, setSelected] = useState(null);

  return (
    <section id="work" ref={ref} className="relative" style={{ height: `${n * 55}vh` }} data-testid="work-section">
      <div className="sticky top-0 h-screen flex flex-col justify-center overflow-hidden relative">
        <div className="max-w-[1500px] w-full mx-auto px-6 lg:px-10 mb-8 flex items-end justify-between">
          <div>
            <div className="overline mb-3">Selected Work</div>
            <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl text-white leading-none">
              A <span className="gold-fill italic">Portfolio</span> in Motion
            </h2>
          </div>
          <p className="hidden md:block text-[var(--text-2)] text-sm max-w-xs text-right">Tap any project for the full story. On projects with a gold handle, drag to wipe between finished and under-construction.</p>
        </div>
        <motion.div style={{ x }} className="flex gap-6 lg:gap-8 px-6 lg:px-10 will-change-transform">
          {projects.map((p, i) => (
            <Panel key={i} p={p} progress={scrollYProgress} index={i} count={n} onOpen={setSelected} />
          ))}
        </motion.div>
      </div>

      <AnimatePresence>
        {selected && <CaseView project={selected} onClose={() => setSelected(null)} />}
      </AnimatePresence>
    </section>
  );
}
